/**
 * ═══════════════════════════════════════════════════════════════════════════
 * OX DÜYMƏLƏRİ İLƏ FOKUS — BRAUZER VARİANTI (tv.kinobox.az, masaüstü klaviatura)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠ NƏ ÖLÇÜLDÜ (2026-10-01, TestingAI2 veb TV 29, Playwright Chromium 1440×900):
 *   Tab fokusu kartdan-karta keçirirdi, amma ox düymələri heç bir elementə fokus
 *   vermirdi — yalnız səhifə sürüşürdü (`document.activeElement` dəyişmirdi).
 *   Səbəb `browser/shell.ts` → `bindKeyboard`: oxlar QƏSDƏN brauzerə buraxılmışdı,
 *   çünki TV-nin öz brauzeri onları özü udur və `keydown` sənədə çatmır
 *   (2026-08-10 real cihaz testi). Masaüstü brauzerdə isə hadisə ÇATIR və oxlar
 *   standart olaraq yalnız sürüşdürür — yəni klaviatura ilə gəzmək mümkün deyildi.
 *
 * ⚠ NİYƏ `lib/focus.ts` (cihazın fokus mühərriki) DEYİL: o, sabit 1920×1080
 *   kətanın «sətir × sütun» indeks şəbəkəsidir və hər ekran onu öz kart
 *   massivləri ilə qurur. Brauzer variantının ekranları doğma axında çəkilir
 *   (sınıq nöqtələr, sətir sarması, doğma sürüşmə) — indeks şəbəkəsi burada
 *   mövcud deyil. Ona görə istiqamət HƏNDƏSİ seçilir (W3C CSS Spatial Navigation
 *   qaydasının sadə forması): `lib/focus.ts`-in qaçdığı `getBoundingClientRect`
 *   bu variantda yalnız ox basılanda oxunur və hadisə TV brauzerinə çatmadığı
 *   üçün zəif cihazda heç vaxt işləmir.
 *
 * ⚠ SİÇAN TOXUNULMUR: modul yalnız `keydown`-a cavab verir; klik, hover və təkər
 *   əvvəlki kimidir. İstiqamətdə element yoxdursa hadisə UDULMUR — səhifə
 *   başında/sonunda ox əvvəlki kimi sürüşdürür. Tab doğma qalır.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export type SpatialDirection = 'up' | 'down' | 'left' | 'right'

const FOCUSABLE =
  'summary, a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

interface Box {
  left: number
  right: number
  top: number
  bottom: number
  cx: number
  cy: number
}

function boxOf(node: Element): Box | null {
  const r = node.getBoundingClientRect()
  if (r.width <= 0 || r.height <= 0) return null
  return { left: r.left, right: r.right, top: r.top, bottom: r.bottom, cx: (r.left + r.right) / 2, cy: (r.top + r.bottom) / 2 }
}

/** Görünən (gizli olmayan) fokuslana bilən elementlər — `scope` daxilində. */
function candidates(scope: ParentNode): HTMLElement[] {
  const list = scope.querySelectorAll(FOCUSABLE)
  const out: HTMLElement[] = []
  for (let i = 0; i < list.length; i++) {
    const node = list[i] as HTMLElement
    if (node.closest('[hidden], [aria-hidden="true"], [inert]')) continue
    out.push(node)
  }
  return out
}

/**
 * İstiqamətdəki ən yaxın namizəd.
 *
 * Xal = əsas oxdakı məsafə + 2 × yan sürüşmə; əsas oxda proyeksiyası üst-üstə
 * düşən namizəd (eyni sıra/sütun) yan sürüşməsiz sayılır — sıradan sıraya
 * keçəndə fokus ən yaxın kartda qalır, diaqonala tullanmır.
 */
function pick(from: Box, nodes: HTMLElement[], current: HTMLElement | null, dir: SpatialDirection): HTMLElement | null {
  let best: HTMLElement | null = null
  let bestScore = Infinity
  for (const node of nodes) {
    if (node === current || (current && (node.contains(current) || current.contains(node)))) continue
    const b = boxOf(node)
    if (!b) continue

    let main: number
    let side: number
    if (dir === 'right' || dir === 'left') {
      main = dir === 'right' ? b.left - from.right : from.left - b.right
      // Namizəd cari elementin MƏRKƏZİNDƏN o tərəfdə başlamalıdır — sıranın üstünə
      // qoyulmuş sürüşmə oxları («Geri sürüşdür») qonşu kart sayılmasın.
      if (dir === 'right' ? b.left < from.cx : b.right > from.cx) continue
      const overlap = Math.min(b.bottom, from.bottom) - Math.max(b.top, from.top)
      side = overlap > 0 ? 0 : Math.abs(b.cy - from.cy)
    } else {
      main = dir === 'down' ? b.top - from.bottom : from.top - b.bottom
      if (dir === 'down' ? b.top < from.cy : b.bottom > from.cy) continue
      const overlap = Math.min(b.right, from.right) - Math.max(b.left, from.left)
      // Şaquli keçiddə sol kənarın yaxınlığı da sayılır — sıradakı «ilk görünən»
      // kart yox, cari kartın altındakı kart seçilsin.
      side = overlap > 0 ? Math.abs(b.left - from.left) / 4 : Math.abs(b.cx - from.cx)
    }
    const score = Math.max(main, 0) + 2 * side
    if (score < bestScore) {
      bestScore = score
      best = node
    }
  }
  return best
}

/**
 * Fokusu istiqamətdə növbəti elementə keçirir. Keçid olubsa `true` (çağıran
 * hadisəni udur), olmayıbsa `false` (brauzerin doğma sürüşməsi işləsin).
 *
 * Fokus heç yerdə deyilsə (səhifə yeni açılıb, siçanla klik olunub) ekranda
 * görünən BİRİNCİ elementə düşür — əks halda ilk ox heç nə etmirdi.
 */
export function moveFocus(dir: SpatialDirection, scope: ParentNode): boolean {
  const nodes = candidates(scope)
  if (nodes.length === 0) return false

  const active = document.activeElement as HTMLElement | null
  const inScope = active && active !== document.body && (scope as Node).contains(active) ? active : null

  let target: HTMLElement | null = null
  if (inScope) {
    const from = boxOf(inScope)
    if (from) target = pick(from, nodes, inScope, dir)
  } else {
    const height = window.innerHeight || document.documentElement.clientHeight
    for (const node of nodes) {
      const b = boxOf(node)
      if (b && b.bottom > 0 && b.top < height) {
        target = node
        break
      }
    }
  }
  if (!target) return false

  target.focus()
  revealNearest(target)
  return true
}

/*
  2026-10-23: `scrollIntoView({ block: 'nearest' })` ƏVƏZİNƏ `scrollIntoViewIfNeeded(false)`.
    NƏ TAPILDI: seçim obyekti Chrome 61+-dır. Chrome 53 (webOS 4.0) obyekti `alignToTop`
    bulevi kimi oxuyur — obyekt «doğru»dur, yəni `scrollIntoView(true)` işləyirdi.
    ƏVVƏL: HƏR ox basışında fokuslanan element pəncərənin YUXARI kənarına yapışdırılırdı —
    görünən kartda belə səhifə tullanırdı.
    NİYƏ BELƏ: `scrollIntoViewIfNeeded` WebKit/Blink-in köhnə metodudur (Chrome 53-də var):
    element görünürsə heç nə etmir, `false` isə ən yaxın kənara hizalayır — `nearest`-in
    eyni mənası. Metod olmayan mühərrikdə `focus()`-un öz sürüşməsi qalır.
*/
function revealNearest(target: HTMLElement): void {
  const legacy = target as HTMLElement & { scrollIntoViewIfNeeded?: (center?: boolean) => void }
  if (typeof legacy.scrollIntoViewIfNeeded === 'function') legacy.scrollIntoViewIfNeeded(false)
}
